import React, { useState } from 'react';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Eye, Users, Mic, ArrowRight } from 'lucide-react';

const SERVICES = [
  {
    icon: Eye,
    title: 'Portfolio Review & Positioning',
    description: 'Your portfolio is more than a collection of work \u2014 it\u2019s how the industry sees you. We review your portfolio, Instagram, or website and help you refine your positioning, presentation, and direction.',
    includes: [
      'Portfolio or Instagram review',
      'Feedback on positioning and narrative',
      'Suggestions for improvement and opportunities'
    ],
    pricing: 'Starting from \u20b9999 (custom depending on depth)'
  },
  {
    icon: Users,
    title: 'Curated Talent Sourcing',
    description: 'We help galleries, brands, and cultural organisations find the right creative talent \u2014 from curators and designers to producers and strategists.',
    includes: [
      'Understanding role requirements',
      'Shortlisting curated candidates',
      'Matching based on culture and fit'
    ],
    pricing: 'Projects typically range from \u20b910,000 onwards depending on scope'
  },
  {
    icon: Mic,
    title: 'Speaker & Creative Talent Booking',
    description: 'Looking for a speaker, artist, or creative voice for your event or panel? We connect you with relevant, high-quality talent across the creative ecosystem.',
    includes: [
      'Talent recommendations',
      'Speaker coordination',
      'Cultural alignment with your event'
    ],
    pricing: 'Typically \u20b95,000\u2013\u20b925,000 depending on requirements'
  }
];

const ServicesPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    inquiry_type: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.inquiry_type) {
      toast.error('Please select a type of inquiry.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/inquiries`, formData);
      setSubmitted(true);
      setFormData({ name: '', email: '', inquiry_type: '', message: '' });
    } catch (error) {
      toast.error('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F0EBE2' }}>
      <Navbar />

      {/* Hero */}
      <section className="px-6 md:px-12 lg:px-24 py-24 md:py-36" style={{ backgroundColor: '#0F2942' }}>

        <SEO
          title="Services. Recruitment, Creative & Artist Support"
          path="/services"
          description="Specialist recruitment, retainer-based creative services and career support for artists, museums, galleries and cultural organisations across India."
        />
        <div className="max-w-3xl">
          <h1
            className="text-5xl md:text-6xl font-light tracking-tight leading-tight mb-6 text-white"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            Creative Career & Cultural Consulting
          </h1>
          <div className="w-20 h-px mb-8" style={{ backgroundColor: 'rgba(212,130,10,0.6)' }} />
          <p className="text-base md:text-lg leading-relaxed font-light max-w-2xl" style={{ color: 'rgba(240, 235, 226, 0.65)' }}>
            We work with artists, curators, brands and cultural institutions to unlock opportunities, build positioning, and connect the right people.
          </p>
        </div>
      </section>

      {/* Photo banner */}
      <figure className="relative" data-testid="services-banner-photo">
        <img
          src="https://images.unsplash.com/photo-1719400030798-af7ac5760172?w=1800&q=70&auto=format&fit=crop"
          alt="A group of visitors gathered on the floor in front of a mural. Photo by Neon Wang on Unsplash"
          loading="lazy"
          decoding="async"
          width="1800"
          height="500"
          className="w-full h-[220px] md:h-[300px] object-cover"
        />
      </figure>

      {/* Services */}
      <section className="px-6 md:px-12 lg:px-24 py-20 md:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {SERVICES.map((service, i) => {
            const Icon = service.icon;
            return (
              <div
                key={i}
                className="bg-white rounded-xl p-8 md:p-10 shadow-sm flex flex-col"
                data-testid={`service-card-${i}`}
              >
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center mb-6"
                  style={{ backgroundColor: 'rgba(212,130,10,0.12)' }}
                >
                  <Icon className="w-5 h-5" style={{ color: '#D4820A' }} strokeWidth={1.5} />
                </div>

                <h3
                  className="text-2xl font-normal tracking-tight mb-4"
                  style={{ fontFamily: 'Cormorant Garamond, serif', color: '#0F2942' }}
                >
                  {service.title}
                </h3>

                <p className="text-sm leading-relaxed mb-6" style={{ color: '#0F2942' }}>
                  {service.description}
                </p>

                <div className="mb-6 flex-1">
                  <p className="text-xs uppercase tracking-[0.2em] font-medium mb-3" style={{ color: '#0F2942' }}>
                    Includes
                  </p>
                  <ul className="space-y-2">
                    {service.includes.map((item, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm" style={{ color: '#0F2942' }}>
                        <ArrowRight className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: '#D4820A' }} strokeWidth={2} />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="pt-5" style={{ borderTop: '1px solid #F0EBE2' }}>
                  <p className="text-sm font-medium" style={{ color: '#0F2942' }}>
                    {service.pricing}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Why Work With Us */}
      <section className="px-6 md:px-12 lg:px-24 py-20 md:py-24" style={{ backgroundColor: '#F0EBE2' }}>
        <div className="max-w-3xl mx-auto text-center">
          <h2
            className="text-3xl md:text-4xl font-normal tracking-tight mb-4"
            style={{ fontFamily: 'Cormorant Garamond, serif', color: '#0F2942' }}
          >
            Why Work With Us
          </h2>
          <div className="w-12 h-px mx-auto mb-8" style={{ backgroundColor: '#D4820A' }} />
          <p className="text-base md:text-lg leading-relaxed font-light" style={{ color: '#0F2942' }}>
            We&rsquo;re not a generic platform. Our strength lies in curation, taste, and deep understanding of the creative ecosystem in India.
          </p>
        </div>
      </section>

      {/* Inquiry Form */}
      <section className="px-6 md:px-12 lg:px-24 py-20 md:py-28" data-testid="inquiry-section">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-12">
            <h2
              className="text-3xl md:text-4xl font-normal tracking-tight mb-4"
              style={{ fontFamily: 'Cormorant Garamond, serif', color: '#0F2942' }}
            >
              Get in Touch
            </h2>
            <div className="w-12 h-px mx-auto mb-6" style={{ backgroundColor: '#D4820A' }} />
            <p className="text-base font-light" style={{ color: '#0F2942' }}>
              Tell us what you need. We&rsquo;ll respond within 24 to 48 hours.
            </p>
          </div>

          {submitted ? (
            <div className="bg-white rounded-xl p-12 shadow-sm text-center" data-testid="inquiry-success">
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6"
                style={{ backgroundColor: 'rgba(212,130,10,0.12)' }}
              >
                <svg className="w-7 h-7" style={{ color: '#D4820A' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3
                className="text-2xl font-normal tracking-tight mb-3"
                style={{ fontFamily: 'Cormorant Garamond, serif', color: '#0F2942' }}
              >
                Inquiry Submitted
              </h3>
              <p className="text-sm" style={{ color: '#0F2942' }}>
                We&rsquo;ll get back to you within 24 to 48 hours.
              </p>
              <button
                onClick={() => setSubmitted(false)}
                className="mt-6 text-sm font-medium hover:underline"
                style={{ color: '#0F2942' }}
                data-testid="send-another-inquiry"
              >
                Send another inquiry
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="bg-white rounded-xl p-8 md:p-10 shadow-sm space-y-6" data-testid="inquiry-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="text-sm font-medium mb-2 block" style={{ color: '#0F2942' }}>Name *</label>
                  <Input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="rounded-lg"
                    placeholder="Your name"
                    data-testid="inquiry-name"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block" style={{ color: '#0F2942' }}>Email *</label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="rounded-lg"
                    placeholder="you@example.com"
                    data-testid="inquiry-email"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block" style={{ color: '#0F2942' }}>Type of Inquiry *</label>
                <Select value={formData.inquiry_type} onValueChange={(val) => setFormData({ ...formData, inquiry_type: val })}>
                  <SelectTrigger className="rounded-lg" data-testid="inquiry-type-select">
                    <SelectValue placeholder="Select inquiry type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Portfolio Review">Portfolio Review</SelectItem>
                    <SelectItem value="Talent Sourcing">Talent Sourcing</SelectItem>
                    <SelectItem value="Speaker Booking">Speaker Booking</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block" style={{ color: '#0F2942' }}>Message *</label>
                <Textarea
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  required
                  rows={5}
                  className="rounded-lg"
                  placeholder="Tell us about your needs..."
                  data-testid="inquiry-message"
                />
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="rounded-lg px-8 py-3 font-medium w-full md:w-auto"
                style={{ backgroundColor: '#0F2942', color: '#FFFFFF' }}
                data-testid="inquiry-submit"
              >
                {submitting ? 'Submitting...' : 'Submit Inquiry'}
              </Button>
            </form>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServicesPage;
